import { Box, VStack } from "@chakra-ui/react";
import { useEffect } from "react";
import { Configure, InstantSearch, useStats } from "react-instantsearch";
import type { SearchClient } from "algoliasearch";
import type { AdCampaignSortOption } from "./utils";
import { sortToIndexSuffix } from "./utils";
import {
  DRAFT_CAMPAIGNS_HITS_PER_PAGE,
  DRAFT_ONLY_FACET_FILTERS,
} from "./adsCampaignAlgoliaFilters";
import CampaignsTableHeader from "./CampaignsTableHeader";
import CampaignsTableHits from "./CampaignsTableHits";
import CampaignsTablePagination from "./CampaignsTablePagination";

export type CampaignsTableVariant = "active" | "past" | "draft";

export default function CampaignsTableAlgolia({
  searchClient,
  indexName,
  title,
  sortOrder,
  onSortChange,
  numericFilters,
  facetFilters,
  variant = "active",
  showPerformanceIndicator = true,
  showLegend,
  hideWhenEmpty = false,
  excludedCampaignIds,
  onDeleteDraft,
  deletingCampaignId,
  onVisibilityChange,
}: {
  searchClient: SearchClient;
  indexName: string;
  title: string;
  sortOrder: AdCampaignSortOption;
  onSortChange: (v: AdCampaignSortOption) => void;
  numericFilters?: (string | string[])[];
  facetFilters?: (string | string[])[];
  variant?: CampaignsTableVariant;
  showPerformanceIndicator?: boolean;
  showLegend?: boolean;
  hideWhenEmpty?: boolean;
  excludedCampaignIds?: ReadonlySet<string>;
  onDeleteDraft?: (campaignId: string) => void;
  deletingCampaignId?: string | null;
  /** Called when `hideWhenEmpty` hides or shows this table (e.g. draft section divider). */
  onVisibilityChange?: (visible: boolean) => void;
}) {
  const suffix = sortToIndexSuffix(sortOrder);
  const activeIndex = suffix ? `${indexName}_${suffix}` : indexName;
  const isDraft = variant === "draft";
  const resolvedFacetFilters = isDraft
    ? DRAFT_ONLY_FACET_FILTERS
    : facetFilters;
  const hasNumericFilters = numericFilters != null && numericFilters.length > 0;
  const hasFacetFilters =
    resolvedFacetFilters != null && resolvedFacetFilters.length > 0;
  const resolvedShowLegend =
    showLegend ?? (variant === "active" && showPerformanceIndicator !== false);
  const resolvedShowPerformanceIndicator =
    variant === "active" && showPerformanceIndicator !== false;

  return (
    <InstantSearch searchClient={searchClient} indexName={activeIndex}>
      <Configure
        numericFilters={hasNumericFilters ? numericFilters : undefined}
        facetFilters={hasFacetFilters ? resolvedFacetFilters : undefined}
        hitsPerPage={isDraft ? DRAFT_CAMPAIGNS_HITS_PER_PAGE : undefined}
      />
      <CampaignsTableAlgoliaBody
        title={title}
        sortOrder={sortOrder}
        onSortChange={onSortChange}
        variant={variant}
        hideWhenEmpty={hideWhenEmpty}
        showLegend={resolvedShowLegend}
        showPerformanceIndicator={resolvedShowPerformanceIndicator}
        excludedCampaignIds={excludedCampaignIds}
        onDeleteDraft={onDeleteDraft}
        deletingCampaignId={deletingCampaignId}
        onVisibilityChange={onVisibilityChange}
      />
    </InstantSearch>
  );
}

function CampaignsTableAlgoliaBody({
  title,
  sortOrder,
  onSortChange,
  variant,
  hideWhenEmpty,
  showLegend,
  showPerformanceIndicator,
  excludedCampaignIds,
  onDeleteDraft,
  deletingCampaignId,
  onVisibilityChange,
}: {
  title: string;
  sortOrder: AdCampaignSortOption;
  onSortChange: (v: AdCampaignSortOption) => void;
  variant: CampaignsTableVariant;
  hideWhenEmpty: boolean;
  showLegend?: boolean;
  showPerformanceIndicator: boolean;
  excludedCampaignIds?: ReadonlySet<string>;
  onDeleteDraft?: (campaignId: string) => void;
  deletingCampaignId?: string | null;
  onVisibilityChange?: (visible: boolean) => void;
}) {
  const { nbHits } = useStats();
  const excludedCount = excludedCampaignIds?.size ?? 0;
  const effectiveNbHits = Math.max(0, nbHits - excludedCount);
  const isVisible = !(hideWhenEmpty && effectiveNbHits === 0);

  useEffect(() => {
    if (!hideWhenEmpty || !onVisibilityChange) return;
    onVisibilityChange(isVisible);
    return () => onVisibilityChange(false);
  }, [hideWhenEmpty, isVisible, onVisibilityChange]);

  if (!isVisible) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <CampaignsTableHeader
        title={title}
        sortOrder={sortOrder}
        onSortChange={onSortChange}
        showLegend={showLegend}
      />
      <Box
        overflowX="auto"
        borderWidth="1px"
        borderRadius="2xl"
        borderColor="gray.200"
        bg="white"
        boxShadow="sm"
      >
        <CampaignsTableHits
          variant={variant}
          showPerformanceIndicator={showPerformanceIndicator}
          isPastCampaigns={variant === "past"}
          excludedCampaignIds={excludedCampaignIds}
          onDeleteDraft={onDeleteDraft}
          deletingCampaignId={deletingCampaignId}
        />
        {variant === "draft" ? <CampaignsTablePagination /> : null}
      </Box>
    </VStack>
  );
}
